import { useState } from 'react';
import { User } from 'lucide-react';
import { PeopleProfileDialog, PeopleProfileData } from './PeopleProfileDialog';
import { trackEvent, ANALYTICS_EVENTS } from '@/lib/analytics';
import { optimizeImage } from '@/lib/imageOptimization';
import { useSmartImage } from '@/hooks/useSmartImage';

export interface AuthorCardProps {
  /** Author name */
  name: string;
  /** Avatar image URL */
  avatarUrl?: string;
  /** Author title/role */
  title?: string;
  /** 
   * Variant style:
   * - 'default': Standard card with bg-grey surface
   * - 'clickable': Adds hover state and opens profile dialog
   * - 'inline': Compact pill for use alongside article meta
   */
  variant?: 'default' | 'clickable' | 'inline';
  /** Full profile data - required for clickable variant to open dialog */
  profile?: PeopleProfileData;
}

/**
 * molecule-author-detailed (default/clickable variants)
 * molecule-author-inline (inline variant)
 * 
 * Author attribution component with two modes:
 * - detailed: Full card with name, title, and optional dialog
 * - inline: Compact byline with small avatar for article headers
 */
export function AuthorCard({
  name,
  avatarUrl,
  title,
  variant = 'default',
  profile,
}: AuthorCardProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { ref, inView } = useSmartImage();

  const handleClick = () => {
    // ⚠️ ANALYTICS: Author engagement tracking
    trackEvent(ANALYTICS_EVENTS.AUTHOR_CLICKED, {
      author_name: name,
      context: variant,
    });

    if (variant === 'clickable' && profile) {
      // ⚠️ ANALYTICS: Track modal open for author profile
      trackEvent(ANALYTICS_EVENTS.MODAL_OPENED, {
        modal_id: 'author-profile',
        modal_type: 'profile',
        modal_title: name,
        trigger_page: window.location.pathname,
        trigger_section: 'article-author',
        trigger_element: 'author_pill',
      });
      setDialogOpen(true);
    }
  };

  // Inline variant - compact pill for article meta row
  if (variant === 'inline') {
    const InlineContent = (
      <div ref={ref} className="flex items-center gap-2">
        {inView && avatarUrl ? (
          <img
             src={optimizeImage(avatarUrl, { width: 48 })}
            alt={name}
            loading="lazy"
            decoding="async"
            width={24}
            height={24}
            className="h-6 w-6 rounded-full object-cover object-[center_20%] flex-shrink-0"
          />
        ) : !avatarUrl ? (
          <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
            <User className="h-3 w-3 text-muted-foreground" />
          </div>
        ) : null}
        <span className="text-sm text-foreground font-medium">{name}</span>
      </div>
    );

    if (profile) {
      return (
        <>
          <button
            onClick={handleClick}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            {InlineContent}
          </button>
          <PeopleProfileDialog
            profile={profile}
            open={dialogOpen}
            onOpenChange={setDialogOpen}
          />
        </>
      );
    }

    return InlineContent;
  }

  // Default/clickable variant - full card
  const CardContent = (
    <div ref={ref} className="flex items-center gap-4 bg-grey rounded-full py-2 pl-2 pr-6">
      {inView && avatarUrl ? (
        <img
          src={optimizeImage(avatarUrl, { width: 96 })}
          alt={name}
          loading="lazy"
          decoding="async"
          width={48}
          height={48}
          className="h-12 w-12 rounded-full object-cover object-[center_20%] flex-shrink-0"
        />
      ) : !avatarUrl ? (
        <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
          <User className="h-6 w-6 text-muted-foreground" />
        </div>
      ) : null}
      <div className="flex flex-col">
        <span className="text-base font-semibold text-carbon">{name}</span>
        {title && (
          <span className="text-sm text-coal">{title}</span>
        )}
      </div>
    </div>
  );

  if (variant === 'clickable' && profile) {
    return (
      <>
        <button
          onClick={handleClick}
          className="text-left hover:opacity-90 transition-opacity"
        >
          {CardContent}
        </button>
        <PeopleProfileDialog
          profile={profile}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      </>
    );
  }

  return CardContent;
}

export default AuthorCard;
